import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HamburgerMenu from "../components/HamburgerMenu";
import { getMyBookings } from "../api";
import { formatScheduled } from "../utils/dateFormat";

const POPULAR_SERVICES = [
  { id: "Cleaning", label: "House Cleaning", icon: "🧹", color: "from-blue-500 to-blue-600" },
  { id: "Plumbing", label: "Plumbing", icon: "🔧", color: "from-cyan-500 to-blue-500" },
  { id: "Electrical", label: "Electrical", icon: "⚡", color: "from-yellow-500 to-orange-500" },
  { id: "Painting", label: "Painting", icon: "🎨", color: "from-purple-500 to-pink-500" },
  { id: "Outdoor", label: "Lawn & Yard", icon: "🌿", color: "from-green-500 to-emerald-600" },
  { id: "Moving", label: "Moving", icon: "🚚", color: "from-orange-500 to-red-500" },
  { id: "HVAC", label: "Heating & AC", icon: "❄️", color: "from-sky-500 to-cyan-600" },
  { id: "Roofing", label: "Roofing", icon: "🏠", color: "from-stone-500 to-stone-700" },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<any[]>([]);
  const [userName, setUserName] = useState("there");
  const [searchValue, setSearchValue] = useState("");
  const [topPros, setTopPros] = useState<any[]>([]);

  useEffect(() => {
    try {
      const k = Object.keys(localStorage).find(key => key.includes("idToken"));
      if (k) {
        const token = localStorage.getItem(k);
        if (token) {
       const payload = JSON.parse(atob(token.split(".")[1]));
          setUserName(payload.given_name || payload.email?.split("@")[0] || "there");
        }
      }
    } catch {}

    getMyBookings().then(data => setBookings(data || [])).catch(() => {});

    fetch("https://uofc7ezzuk.execute-api.us-east-1.amazonaws.com/public/featured-pros")
      .then(r => r.json())
      .then(data => setTopPros((data.pros || []).slice(0, 6)))
      .catch(() => {});
  }, []);

  const activeJob = bookings.find(b => b.status === "confirmed" || b.status === "in_progress");

  function handleSearch(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (searchValue.trim()) navigate(`/explore?q=${encodeURIComponent(searchValue)}`);
    else navigate("/explore");
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center gap-3">
          <HamburgerMenu role="client" />
          <div className="flex-1 flex items-center gap-2">
            <svg width="32" height="32" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="hLg1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4C6EF5"/><stop offset="100%" stopColor="#1A3CE8"/></linearGradient>
                <linearGradient id="hLg2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#20C997"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
                <linearGradient id="hBg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
              </defs>
              <rect width="52" height="52" rx="14" fill="url(#hBg)"/>
              <g transform="translate(4,5)"><path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.95"/><circle cx="11" cy="10" r="5" fill="url(#hLg1)"/><circle cx="11" cy="10" r="2.5" fill="white"/></g>
              <g transform="translate(25,5)"><path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.85"/><circle cx="11" cy="10" r="5" fill="url(#hLg2)"/><circle cx="11" cy="10" r="2.5" fill="white"/></g>
            </svg>
            <span className="font-display text-lg font-bold text-navy">Mouna</span>
          </div>
          <button onClick={() => navigate("/profile")}
            className="w-10 h-10 rounded-full bg-blue-pale flex items-center justify-center text-mouna-blue font-bold text-sm">
            {userName.charAt(0).toUpperCase()}
          </button>
        </div>
      </header>

      {/* Hero with search */}
      <div className="bg-gradient-to-br from-mouna-blue via-blue-600 to-navy px-4 pt-8 pb-12 text-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-xs text-blue-200 font-bold uppercase tracking-wider mb-2">👋 Welcome back</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mb-2 leading-tight capitalize">Hi {userName}</h1>
          <p className="text-blue-100 text-sm mb-5">What service do you need help with today?</p>

          <form onSubmit={handleSearch} className="bg-white rounded-2xl p-1.5 shadow-2xl">
            <div className="flex gap-1">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">🔍</span>
                <input type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)}
                  placeholder="What do you need?"
                  className="w-full pl-10 pr-4 py-3 text-sm outline-none rounded-xl text-navy"/>
              </div>
              <button type="submit" className="bg-mouna-blue hover:bg-blue-700 text-white rounded-xl px-6 py-3 text-sm font-bold transition-colors">
                Search
              </button>
            </div>
          </form>

          <div className="flex items-center gap-4 mt-4 text-xs text-blue-100">
            <span>✓ Verified pros</span>
            <span>✓ Free quotes</span>
            <span>✓ Houston-local</span>
          </div>
        </div>
      </div>

      {/* Active Job */}
      {activeJob && (
        <div className="px-4 -mt-4 mb-4 max-w-6xl mx-auto w-full">
          <div className="bg-white rounded-2xl p-5 shadow-xl border border-blue-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-mouna-blue animate-pulse"></div>
              <span className="text-xs font-bold text-mouna-blue uppercase tracking-wider">
                {activeJob.status === "in_progress" ? "🚗 In Progress" : "✅ Confirmed"}
              </span>
            </div>
            <h2 className="font-bold text-navy text-lg mb-2">{activeJob.serviceType || "Service"}</h2>
            <div className="text-sm text-gray-600 mb-1">📅 {formatScheduled(activeJob.scheduledFor || activeJob.createdAt)}</div>
            <div className="text-sm text-gray-600 mb-4">📍 {activeJob.address || "Address pending"}</div>
            <div className="flex gap-2">
              <button onClick={() => navigate(`/bookings`)} className="flex-1 bg-mouna-blue text-white rounded-xl py-3 text-sm font-bold">View Details</button>
              {activeJob.proId && (
                <button onClick={() => navigate(`/messages?conversation=${activeJob.clientId}_${activeJob.proId}&recipient=${activeJob.proId}&name=Pro`)}
                  className="flex-1 bg-white text-mouna-blue border border-mouna-blue rounded-xl py-3 text-sm font-bold">Message Pro</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Popular services - Angi grid */}
      <div className="px-4 pt-4 pb-6 max-w-6xl mx-auto w-full">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-xl font-bold text-navy">Popular services</h2>
          <button onClick={() => navigate("/explore")} className="text-xs text-mouna-blue font-bold">View all →</button>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {POPULAR_SERVICES.map(svc => (
            <button key={svc.id} onClick={() => navigate(`/explore?category=${svc.id}`)}
              className="group flex flex-col items-center gap-2 p-3 rounded-2xl bg-white hover:shadow-lg transition-all">
              <div className={"w-14 h-14 rounded-2xl bg-gradient-to-br " + svc.color + " flex items-center justify-center text-2xl shadow-md group-hover:scale-110 transition-transform"}>
                {svc.icon}
              </div>
              <span className="text-xs font-bold text-navy text-center leading-tight">{svc.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Top Pros featured row */}
      {topPros.length > 0 && (
        <div className="px-4 pb-8 max-w-6xl mx-auto w-full">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h2 className="font-display text-xl font-bold text-navy">⭐ Top-rated near you</h2>
              <p className="text-xs text-gray-500 mt-0.5">Verified pros customers love</p>
            </div>
            <button onClick={() => navigate("/explore")} className="text-xs text-mouna-blue font-bold">See all →</button>
          </div>

          <div className="flex gap-3 overflow-x-auto -mx-4 px-4 pb-2 scrollbar-hide">
            {topPros.map(pro => (
              <button key={pro.proId} onClick={() => navigate(`/pro/${pro.proId}`)}
                className="flex-shrink-0 w-64 bg-white rounded-2xl shadow-card hover:shadow-lg transition-shadow text-left overflow-hidden">
                <div className="aspect-square bg-gradient-to-br from-blue-pale to-gray-100 relative">
                  {pro.photoUrl ? (
                    <img src={pro.photoUrl} alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl opacity-40">👷</div>
                  )}
                  {pro.verified && <div className="absolute top-2 left-2 bg-white text-teal text-xs font-bold rounded-full px-2 py-1">✓ Verified</div>}
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-navy truncate text-sm">{pro.businessName || pro.name}</h3>
                  <p className="text-xs text-gray-500 truncate mb-1">{pro.serviceCategory}</p>
                  <div className="flex items-center gap-2 text-xs">
                    <span>⭐ {pro.rating?.toFixed(1) || "New"}</span>
                    <span className="text-gray-300">•</span>
                    <span className="text-gray-500">{pro.reviewCount || 0} reviews</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="px-4 pb-8 max-w-6xl mx-auto w-full">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white text-center">
          <div className="text-3xl mb-2">🎯</div>
          <h3 className="font-display text-xl font-bold mb-2">Need a specific pro?</h3>
          <p className="text-sm text-emerald-50 mb-4">Browse local professionals across 25 service categories</p>
          <button onClick={() => navigate("/explore")}
            className="bg-white text-emerald-600 rounded-full px-6 py-3 text-sm font-bold hover:bg-emerald-50 transition-colors">
            Browse all pros →
          </button>
        </div>
      </div>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-2 z-30">
        <div className="flex items-center justify-around max-w-md mx-auto">
          {[
            { icon: "🏠", label: "Home", path: "/home", active: true },
            { icon: "🔍", label: "Explore", path: "/explore" },
            { icon: "📅", label: "Bookings", path: "/bookings" },
            { icon: "👤", label: "Profile", path: "/profile" },
          ].map(nav => (
            <button key={nav.label} onClick={() => navigate(nav.path)}
              className={"flex flex-col items-center gap-0.5 px-3 py-2 " + (nav.active ? "text-mouna-blue" : "text-gray-400")}>
              <span className="text-xl">{nav.icon}</span>
              <span className="text-xs font-bold">{nav.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
